import { addElement, animate, easeIn, nap, px, spring, stageSize, type DemoImpl } from '@/components/motion/demos/demoKit'

/* Chapter 3: offers move through the accountant's ring into the client's card. */

/** [label, angle in degrees around the ring, reaches the client] */
const offers: Array<[string, number, boolean]> = [
  ['Cash planning', 160, true],
  ['Credit line', 200, false],
  ['Tax planning', 20, true],
  ['Insurance review', 340, false],
  ['Retirement plans', 122, false],
  ['Sale planning', 58, false],
]

type OrbitContext = {
  stage: HTMLElement
  ring: HTMLElement
  ringLabel: HTMLElement
  offers: HTMLElement[]
  client: HTMLElement
}

type OrbitGeometry = {
  cx: number
  cy: number
  diameter: number
  cardWidth: number
  cardTop: number
  positions: Array<[number, number, number]>
}

const geometry = (context: OrbitContext): OrbitGeometry => {
  const { width, height } = stageSize(context.stage)
  const cx = width / 2
  const cy = height / 2 - 4
  const diameter = Math.min(width * 0.5, height * 0.64)
  const cardWidth = Math.min(196, Math.max(150, diameter * 0.74))
  const rx = diameter / 2 + 72
  const ry = diameter / 2 + 44
  const positions = offers.map(([, angle], index): [number, number, number] => {
    const radians = (angle * Math.PI) / 180
    const half = (context.offers[index] ? context.offers[index].offsetWidth : 110) / 2
    const x = Math.max(half + 10, Math.min(width - half - 10, cx + Math.cos(radians) * rx))
    return [x, cy + Math.sin(radians) * ry, radians]
  })
  return { cx, cy, diameter, cardWidth, cardTop: cy - 72, positions }
}

const addRecommendation = (client: HTMLElement, label: string) =>
  addElement('div', 'm-rec', client, `<span>${label}</span><small>From your accountant</small>`)

const place = (context: OrbitContext, g: OrbitGeometry, done: boolean) => {
  Object.assign(context.ring.style, {
    width: px(g.diameter),
    height: px(g.diameter),
    left: px(g.cx - g.diameter / 2),
    top: px(g.cy - g.diameter / 2),
    opacity: '1',
  })
  Object.assign(context.ringLabel.style, { left: px(g.cx), top: px(g.cy - g.diameter / 2), opacity: '1' })
  Object.assign(context.client.style, { left: px(g.cx), top: px(g.cardTop), width: px(g.cardWidth) })
  context.client.querySelectorAll('.m-rec').forEach((recommendation) => recommendation.remove())
  context.offers.forEach((offer, index) => {
    const [x, y] = g.positions[index]
    Object.assign(offer.style, { left: px(x), top: px(y), boxShadow: '' })
    offer.style.opacity = done ? (offers[index][2] ? '0' : '0.45') : '1'
  })
  if (done) {
    for (const [label, , picked] of offers) if (picked) addRecommendation(context.client, label)
  }
}

export const orbitDemo: DemoImpl<OrbitContext> = {
  setup: (stage) => ({
    stage,
    ring: addElement('div', 'm-ring', stage),
    ringLabel: addElement('span', 'm-ring-label', stage, 'Your accountant'),
    offers: offers.map(([label]) => addElement('span', 'm-offer', stage, label)),
    client: addElement('div', 'm-client', stage, '<small class="m-client-k">Your client</small><b>Brightside Bakery</b>'),
  }),
  reset: (context) => {
    place(context, geometry(context), false)
    context.ring.style.opacity = '0'
    context.ringLabel.style.opacity = '0'
    context.offers.forEach((offer) => {
      offer.style.opacity = '0'
    })
  },
  final: (context) => place(context, geometry(context), true),
  play: async (context, ok) => {
    const g = geometry(context)
    animate(context.ring, [{ opacity: 0, transform: 'scale(.86)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 700, easing: spring })
    animate(
      context.ringLabel,
      [{ opacity: 0, transform: 'translate(-50%, -30%)' }, { opacity: 1, transform: 'translate(-50%, -50%)' }],
      { duration: 400, delay: 250 },
    )
    context.offers.forEach((offer, index) => {
      const radians = g.positions[index][2]
      animate(
        offer,
        [
          { opacity: 0, transform: `translate(calc(-50% + ${Math.cos(radians) * 60}px), calc(-50% + ${Math.sin(radians) * 40}px))` },
          { opacity: 1, transform: 'translate(-50%, -50%)' },
        ],
        { duration: 900, delay: 300 + index * 90 },
      )
    })
    await nap(1600)
    ok()
    let recommendationY = 0
    for (const [index, [label, , picked]] of offers.entries()) {
      if (!picked) continue
      const [x, y, radians] = g.positions[index]
      const edge = [g.cx + Math.cos(radians) * (g.diameter / 2), g.cy + Math.sin(radians) * (g.diameter / 2)]
      const atEdge = `translate(calc(-50% + ${edge[0] - x}px), calc(-50% + ${edge[1] - y}px))`
      animate(
        context.ring,
        [
          { boxShadow: '0 0 0 10px rgba(250,189,0,.10)' },
          { boxShadow: '0 0 0 22px rgba(250,189,0,.22)' },
          { boxShadow: '0 0 0 10px rgba(250,189,0,.10)' },
        ],
        { duration: 700 },
      )
      const offer = context.offers[index]
      offer.style.boxShadow = 'inset 0 0 0 1.5px #FABD00'
      await animate(offer, [{ transform: 'translate(-50%, -50%)' }, { transform: atEdge }], { duration: 650, easing: 'cubic-bezier(.5,0,.2,1)' }).finished
      ok()
      await nap(250)
      ok()
      const intoCard = `translate(calc(-50% + ${g.cx - x}px), calc(-50% + ${g.cardTop + 70 + recommendationY - y}px)) scale(.8)`
      await animate(offer, [{ transform: atEdge, opacity: 1 }, { opacity: 0, offset: 0.55 }, { transform: intoCard, opacity: 0 }], {
        duration: 520,
        easing: easeIn,
      }).finished
      ok()
      const recommendation = addRecommendation(context.client, label)
      animate(recommendation, [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: spring })
      recommendationY += 36
      await nap(450)
      ok()
    }
    context.offers.forEach((offer, index) => {
      if (!offers[index][2]) animate(offer, [{ opacity: 1 }, { opacity: 0.45 }], { duration: 600 })
    })
  },
}
